import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { 
  AppState, 
  ProcessingStatus, 
  ProcessingResult, 
  Notification, 
  DetectionConfig,
  VideoMetadata 
} from '../types';
import { generateId } from '../utils/helpers';

const defaultConfig: DetectionConfig = {
  confidence_threshold: 0.5,
  iou_threshold: 0.45,
  pixels_per_meter: 10,
  speed_unit: 'kmh',
  vehicle_classes: ['car', 'truck', 'bus', 'motorcycle', 'bicycle', 'van', 'pickup', 'suv', 'trailer', 'emergency']
};

export const useStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // File state
        file: null,
        videoMetadata: null,

        // Processing state
        status: 'idle' as ProcessingStatus,
        progress: 0,
        jobId: null,

        // Results
        result: null,

        // UI state
        notifications: [],
        config: defaultConfig,

        // Actions
        setFile: (file: File | null) => set({ file }),
        
        setVideoMetadata: (metadata: VideoMetadata | null) => 
          set({ videoMetadata: metadata }),

        setStatus: (status: ProcessingStatus) => set({ status }),
        
        setProgress: (progress: number) => set({ progress }),
        
        setJobId: (jobId: string | null) => set({ jobId }),
        
        setResult: (result: ProcessingResult | null) => set({ result }),

        addNotification: (notification: Omit<Notification, 'id'>) =>
          set((state) => ({
            notifications: [
              ...state.notifications,
              { ...notification, id: generateId() }
            ]
          })),

        removeNotification: (id: string) =>
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id)
          })),

        setConfig: (config: Partial<DetectionConfig>) =>
          set((state) => ({
            config: { ...state.config, ...config }
          })),

        reset: () =>
          set({
            file: null,
            videoMetadata: null,
            status: 'idle',
            progress: 0,
            jobId: null,
            result: null
          })
      }),
      {
        name: 'vehicle-detector-storage',
        partialize: (state) => ({ config: state.config })
      }
    )
  )
);

export default useStore;
